import { Card, Button, Space, message } from 'antd';
import PublishActions from './PublishActions';
import PublishModePanel from './publish/PublishModePanel';
import DeleteArticleButton from './DeleteArticleButton';
import SaveStatusText from './SaveStatusText';
import StatusBadge from '../pages/article-list/StatusBadge';
import type { EditorState, SaveIntent } from '../hooks/useArticleEditor';
import type { AutosaveResult } from '../hooks/useAutosave';

interface Props {
  id: string;
  state: EditorState;
  save: { isPending: boolean; variables?: SaveIntent; mutate: (intent: SaveIntent) => void };
  autosave: AutosaveResult;
  publicUrl: string | null;
}

/**
 * 發佈設定卡片：目前狀態、發佈方式（立即／預排）、預覽與連結、刪除。
 * 每個步驟只有一顆主要按鈕（立即發佈 或 確認排程）；內容由自動儲存處理，
 * 「立即儲存」降為自動儲存狀態旁的小連結，不和發佈按鈕搶（客戶 9/5：儲存／發佈並存太複雜）。
 */
export default function PublishSettingsCard({ id, state, save, autosave, publicUrl }: Props) {
  const pending = save.isPending ? (save.variables?.kind ?? null) : null;

  return (
    <Card id="publish-card" className="scroll-mt-3" title="發佈設定" extra={<StatusBadge status={state.status} />}>
      <Space direction="vertical" className="w-full">
        {state.status !== 'published' && (
          <PublishModePanel
            status={state.status}
            scheduledAt={state.scheduledAt}
            pending={pending}
            onAction={(intent) => save.mutate(intent)}
            onInvalid={(msg) => message.warning(msg)}
          />
        )}
        <div className="flex items-center justify-between gap-2 rounded-md bg-black/[0.02] px-2 py-1">
          <SaveStatusText autosave={autosave} />
          <Button
            type="link"
            size="small"
            className="!px-0"
            loading={pending === 'content'}
            disabled={pending !== null}
            onClick={() => save.mutate({ kind: 'content' })}
          >
            立即儲存
          </Button>
        </div>
        <p className="m-0 text-xs text-[#8a94a6]">
          {state.status === 'published'
            ? '修改會自動儲存，並直接更新已上線的文章。'
            : '內容會自動儲存，不會改變發佈狀態。'}
        </p>
        <PublishActions
          id={id}
          title={state.title}
          bodyJson={state.bodyJson}
          relatedIds={state.relatedIds}
          publicUrl={publicUrl}
        />
        <div className="mt-1 border-t border-black/[0.06] pt-3">
          <DeleteArticleButton id={id} title={state.title} />
        </div>
      </Space>
    </Card>
  );
}
